/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { useForm, useWatch } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";

import WalletIcon from "../../../assets/icons/wallet-icon.svg";
import LoadedWalletIcon from "../../../assets/icons/loaded-wallet-icon.svg";
import { useWalletContext } from "../../../utils/Contexts/V2/WalletContext";
import SwitchAccountIcon from "../../../assets/icons/switchBranch.svg";
import { useAuth } from "../../../utils/Contexts/V2/AuthenticationContext";
import useDebounce from "../../../utils/useDebounce";
import {
  API_PATHS,
  searchAccessibleMerchantAccounts,
} from "../../../utils/requests-v2";
import Spinner from "../../Spinner";

// Header displayed for all screens that require the notification and wallet icon.
// You must pass the screen title as headerTitle and set header to true no the Layout component.
function NavBar({ headerTitle, setShowRedeemOfferPrompt }) {
  const { walletDetails } = useWalletContext();
  const { switchAccount, accessibleMerchantAccounts, getRefreshToken } =
    useAuth();
  // const navigate = useNavigate();

  const [displayMerchantAccounts, setDisplayMerchantAccounts] = useState(false);

  const { register, control } = useForm({
    defaultValues: {
      searchString: "",
    },
  });

  const businessName = useWatch({ control, name: "searchString" });

  const debouncedBusinessName = useDebounce(businessName, 1000);

  const queryParams = new URLSearchParams({
    limit: 100,
    offset: 0,
    business_name: debouncedBusinessName,
  });

  const [filteredMerchants, setFilteredMerchants] = useState(null);

  useEffect(() => {
    if (
      accessibleMerchantAccounts &&
      accessibleMerchantAccounts?.items?.length > 0
    ) {
      setFilteredMerchants(accessibleMerchantAccounts);
    }
  }, [accessibleMerchantAccounts]);

  const { isFetching } = useQuery(
    [
      API_PATHS.searchAccessibleMerchantAccounts,
      queryParams.toString(),
      debouncedBusinessName,
    ],
    async () => {
      try {
        const merchants = await searchAccessibleMerchantAccounts(
          debouncedBusinessName,
          100,
          0
        );
        setFilteredMerchants(merchants.data);
        return merchants;
      } catch (error) {
        return null;
      }
    },
    {
      enabled: !!debouncedBusinessName,
    }
  );

  useEffect(() => {
    if (!debouncedBusinessName) {
      setFilteredMerchants(accessibleMerchantAccounts);
    }
  }, [debouncedBusinessName]);

  const onDisplayAccessibleMerchantAccounts = () => {
    setDisplayMerchantAccounts(!displayMerchantAccounts);
  };

  const handleSwitchMerchantAccount = async (merchantId) => {
    const refreshToken = getRefreshToken();
    try {
      await switchAccount({ id: merchantId, token: refreshToken });
      setDisplayMerchantAccounts(!displayMerchantAccounts);
      window.location.href = "/";
    } catch (error) {
      console.error(error);
    }
  };
  return (
    <header className="w-full relative pt-[1rem] px-4 pb-4 flex flex-row justify-between items-center border-b-[0.09rem] border-[#E7E7E7] bg-white">
      <h1>{headerTitle}</h1>
      <div className="flex flex-row space-x-4 items-center justify-center">
        <button type="button" onClick={() => setShowRedeemOfferPrompt(true)}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4 7V6C4 5.46957 4.21071 4.96086 4.58579 4.58579C4.96086 4.21071 5.46957 4 6 4H8M4 17V18C4 18.5304 4.21071 19.0391 4.58579 19.4142C4.96086 19.7893 5.46957 20 6 20H8M16 4H18C18.5304 4 19.0391 4.21071 19.4142 4.58579C19.7893 4.96086 20 5.46957 20 6V7M16 20H18C18.5304 20 19.0391 19.7893 19.4142 19.4142C19.7893 19.0391 20 18.5304 20 18V17M5 12H19"
              stroke="#101828"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <NavLink to="/wallet">
          {walletDetails?.credit_balance > 0 ? (
            <img src={LoadedWalletIcon} alt="wallet" />
          ) : (
            <img src={WalletIcon} alt="wallet" />
          )}
        </NavLink>

        {/* <SwitchAccount /> */}
        {accessibleMerchantAccounts?.items?.length > 0 && (
          <div className="flex justify-center items-center">
            <button type="button" onClick={onDisplayAccessibleMerchantAccounts}>
              <img src={SwitchAccountIcon} alt="Switch Account" />
            </button>

            <div
              className={`absolute right-[8px] top-[50px] bg-white border border-black px-4 py-2 ${
                displayMerchantAccounts ? "block" : "hidden"
              }`}
            >
              <fieldset disabled={isFetching} aria-disabled={isFetching}>
                <ul className="space-y-2 text-sm max-h-[300px] max-w-[200px] overflow-auto">
                  <input
                    type="text"
                    placeholder="search..."
                    className="h-auto my-2 ml-1 p-2 w-[90%]"
                    {...register("searchString")}
                  />
                  {isFetching ? (
                    <div className="h-24 w-full flex flex-col justify-center items-center">
                      <Spinner />
                    </div>
                  ) : (
                    filteredMerchants?.items?.map((merchantAccount) => (
                      <li
                        key={merchantAccount.id}
                        className="text-black cursor-pointer"
                      >
                        <button
                          type="button"
                          onClick={() => {
                            handleSwitchMerchantAccount(merchantAccount.id);
                          }}
                          className="text-start"
                        >
                          {merchantAccount.business_name}
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              </fieldset>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

// Header displayed for all screens that require the header title and back button.
// You must pass the screen title as headerTitle no the Layout component.
function NavbarOthers({ headerTitle, backTo }) {
  const navigate = useNavigate();
  return (
    <header className="w-full pt-[1rem] px-4 pb-4 flex justify-between items-center border-b-[0.09rem] border-[#E7E7E7] bg-white">
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        onClick={() => navigate(backTo)}
        className="mr-4 cursor-pointer"
      >
        <path
          d="M7.828 11H20V13H7.828L13.192 18.364L11.778 19.778L4 12L11.778 4.22198L13.192 5.63598L7.828 11Z"
          fill="#1A1A1A"
        />
      </svg>
      {typeof headerTitle === "string" ? (
        <h1 className="self-center">{headerTitle}</h1>
      ) : (
        <div className="w-full">{headerTitle}</div>
      )}
      <div className="w-6" />
    </header>
  );
}

// Header displayed only for the Wallet screen. You must pay wallet=true no the Layout component.
function NavbarWallet() {
  const navigate = useNavigate();
  return (
    <header className="w-full pt-[1rem] px-4 pb-4 flex justify-between border-b-[0.09rem] border-[#E7E7E7] bg-white">
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        onClick={() => navigate(-1)}
      >
        <path
          d="M7.828 11H20V13H7.828L13.192 18.364L11.778 19.778L4 12L11.778 4.22198L13.192 5.63598L7.828 11Z"
          fill="#1A1A1A"
        />
      </svg>
      <h1 className="self-center">Tappi Wallet</h1>
      <button type="button" onClick={() => window.location.reload()}>
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M15.592 11.925H11.817C11.596 11.925 11.384 12.0128 11.2277 12.1691C11.0715 12.3253 10.9837 12.5373 10.9837 12.7583C10.9837 12.9793 11.0715 13.1913 11.2277 13.3476C11.384 13.5039 11.596 13.5917 11.817 13.5917H13.817C12.8978 14.5523 11.7123 15.2161 10.413 15.4978C9.11359 15.7795 7.75967 15.6663 6.52514 15.1727C5.29061 14.6791 4.23188 13.8276 3.48498 12.7276C2.73809 11.6277 2.33714 10.3295 2.33366 8.99999C2.33366 8.77898 2.24586 8.56701 2.08958 8.41073C1.9333 8.25445 1.72134 8.16666 1.50033 8.16666C1.27931 8.16666 1.06735 8.25445 0.91107 8.41073C0.75479 8.56701 0.666992 8.77898 0.666992 8.99999C0.671398 10.6273 1.15218 12.2178 2.04999 13.575C2.94781 14.9323 4.22338 15.997 5.71928 16.6377C7.21517 17.2784 8.86594 17.4671 10.4678 17.1805C12.0697 16.8939 13.5527 16.1446 14.7337 15.025V16.5C14.7337 16.721 14.8215 16.933 14.9777 17.0892C15.134 17.2455 15.346 17.3333 15.567 17.3333C15.788 17.3333 16 17.2455 16.1562 17.0892C16.3125 16.933 16.4003 16.721 16.4003 16.5V12.75C16.3983 12.5347 16.313 12.3285 16.1623 12.1747C16.0116 12.0209 15.8072 11.9314 15.592 11.925ZM11.5003 8.99999C11.5003 8.50554 11.3537 8.02219 11.079 7.61106C10.8043 7.19994 10.4138 6.87951 9.95703 6.69029C9.50022 6.50107 8.99755 6.45156 8.5126 6.54803C8.02765 6.64449 7.58219 6.88259 7.23256 7.23222C6.88293 7.58185 6.64482 8.02731 6.54836 8.51226C6.4519 8.99722 6.50141 9.49988 6.69063 9.9567C6.87985 10.4135 7.20028 10.804 7.6114 11.0787C8.02252 11.3534 8.50587 11.5 9.00033 11.5C9.66337 11.5 10.2993 11.2366 10.7681 10.7678C11.2369 10.2989 11.5003 9.66303 11.5003 8.99999ZM8.16699 8.99999C8.16699 8.83517 8.21587 8.67406 8.30743 8.53701C8.399 8.39997 8.52915 8.29316 8.68142 8.23009C8.83369 8.16702 9.00125 8.15051 9.1629 8.18267C9.32455 8.21482 9.47304 8.29419 9.58958 8.41073C9.70612 8.52728 9.78549 8.67576 9.81765 8.83741C9.8498 8.99907 9.8333 9.16662 9.77022 9.31889C9.70715 9.47116 9.60034 9.60131 9.4633 9.69288C9.32626 9.78445 9.16514 9.83332 9.00033 9.83332C8.77931 9.83332 8.56735 9.74553 8.41107 9.58924C8.25479 9.43296 8.16699 9.221 8.16699 8.99999ZM9.00033 0.666656C6.86398 0.67275 4.81157 1.49908 3.26699 2.97499V1.49999C3.26699 1.27898 3.17919 1.06701 3.02291 0.910734C2.86663 0.754454 2.65467 0.666656 2.43366 0.666656C2.21264 0.666656 2.00068 0.754454 1.8444 0.910734C1.68812 1.06701 1.60033 1.27898 1.60033 1.49999V5.24999C1.60033 5.471 1.68812 5.68296 1.8444 5.83925C2.00068 5.99553 2.21264 6.08332 2.43366 6.08332H6.18366C6.40467 6.08332 6.61663 5.99553 6.77291 5.83925C6.92919 5.68296 7.01699 5.471 7.01699 5.24999C7.01699 5.02898 6.92919 4.81701 6.77291 4.66073C6.61663 4.50445 6.40467 4.41666 6.18366 4.41666H4.18366C5.10238 3.45655 6.28707 2.79291 7.58566 2.51091C8.88425 2.22892 10.2375 2.34144 11.4717 2.83404C12.7058 3.32664 13.7647 4.17683 14.5122 5.27549C15.2597 6.37415 15.6619 7.67114 15.667 8.99999C15.667 9.221 15.7548 9.43296 15.9111 9.58924C16.0673 9.74553 16.2793 9.83332 16.5003 9.83332C16.7213 9.83332 16.9333 9.74553 17.0896 9.58924C17.2459 9.43296 17.3337 9.221 17.3337 8.99999C17.3337 7.90564 17.1181 6.82201 16.6993 5.81096C16.2805 4.79991 15.6667 3.88125 14.8929 3.10743C14.1191 2.33361 13.2004 1.71978 12.1894 1.30099C11.1783 0.882205 10.0947 0.666656 9.00033 0.666656Z"
            fill="#1A1A1A"
          />
        </svg>
      </button>
    </header>
  );
}

function Header({
  header,
  headerTitle,
  wallet,
  backTo,
  setShowRedeemOfferPrompt,
}) {
  if (wallet) {
    return <NavbarWallet />;
  }
  if (header) {
    return <NavbarOthers headerTitle={headerTitle} backTo={backTo} />;
  }
  return (
    <NavBar
      headerTitle={headerTitle}
      setShowRedeemOfferPrompt={setShowRedeemOfferPrompt}
    />
  );
}

Header.defaultProps = {
  header: false,
  headerTitle: "",
  wallet: false,
  backTo: -1,
};

Header.propTypes = {
  header: PropTypes.bool,
  headerTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  wallet: PropTypes.bool,
  backTo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setShowRedeemOfferPrompt: PropTypes.func.isRequired,
};

NavBar.propTypes = {
  headerTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  setShowRedeemOfferPrompt: PropTypes.func.isRequired,
};

NavBar.defaultProps = {
  headerTitle: "My Business",
};

NavbarOthers.propTypes = {
  headerTitle: PropTypes.string.isRequired,
  backTo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

NavbarOthers.defaultProps = {
  backTo: -1,
};

export default Header;
